import { LEAD_OUTCOME_LABELS, type LeadOutcome } from '../../types/leadOutcome.js';
import type { CallLogRow } from '../../types/leadCall.js';
import { botNameOf } from '../botName.js';
import { personFirstName } from '../personName.js';
import type { ConversationSummaryView } from '../leadBot/smsClassification.js';

// SPEED TO LEAD STAGE 4C: the pure rules of the single per-lead history and of Sam's "Earlier history" block.
// No I/O, no clock reads.
//
// A LEAD IS (club accountId, E.164 phone). Everything here is keyed on that pair and the server only ever reads
// with a club id it has already scope-checked, so another club's history (even for the same phone) is unreachable.
// ⚠️ No transcript, summary or message text goes in a log or an audit entry: this module builds DATA for display.

export const HISTORY_PAGE_SIZE = 10;
/** Messages shown inside one conversation entry (the newest are kept). */
export const HISTORY_MAX_MESSAGES_PER_CONVERSATION = 60;

export interface HistoryMessage {
  id: string;
  at: string;
  /** `note` = a system event (a missed-call note, "we tried to call"): shown as a note, never as the lead's words. */
  from: 'lead' | 'assistant' | 'note';
  text: string;
  /** An assistant message a PERSON approved or edited before it went out (Kyck staff or the club). Absent on an unreviewed autonomous send. */
  byStaff?: boolean;
  /** The first name of the person who sent it (a text a person wrote from the lead). "Kyck" in the club's view when Kyck staff sent it. */
  byName?: string;
}

export type LeadHistoryEntry =
  | { kind: 'intake'; id: string; at: string; source: string | null; result: string | null }
  | {
      kind: 'conversation';
      id: string;
      at: string;
      state: string;
      outcome: LeadOutcome;
      outcomeLabel: string;
      /** The AI summary and suggested outcome of this conversation, when a classification is ready. */
      summary: ConversationSummaryView | null;
      messages: HistoryMessage[];
      messageCount: number;
      truncated: boolean;
    }
  | { kind: 'call'; id: string; at: string; row: CallLogRow }
  | { kind: 'event'; id: string; at: string; event: 'do_not_contact' | 'opted_out' | 'opted_in'; note: string | null }
  // A PERSON'S action on the lead record (Phase 2): the outcome set, or the "needs a person" flag turned on or off. From the lead's
  // own audit entries; a label and a code only, never a transcript, a number or a note.
  | { kind: 'lead_event'; id: string; at: string; event: 'outcome_set' | 'needs_person_on' | 'needs_person_off'; outcome: LeadOutcome | null; by: string };

export interface LeadHistoryPage {
  /** The club's bot name, for "Mia:" on its messages. Null when the club has none (the UI says "the assistant"). */
  assistantName: string | null;
  entries: LeadHistoryEntry[];
  /** Opaque cursor for the next (older) page, or null at the end. */
  nextCursor: string | null;
  total: number;
}

const ms = (iso: string): number => {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
};

// At the SAME instant the enquiry is the OLDEST thing (it caused the call and the conversation), so newest-first lists it
// last; an event is the newest. Then by id, so the order (and so the cursor) is total and stable.
const KIND_RANK: Record<LeadHistoryEntry['kind'], number> = { lead_event: 0, event: 0, call: 1, conversation: 2, intake: 3 };

/** Newest first; ties broken by kind rank then id. */
export function mergeHistory(entries: LeadHistoryEntry[]): LeadHistoryEntry[] {
  return [...entries].sort((a, b) => ms(b.at) - ms(a.at) || KIND_RANK[a.kind] - KIND_RANK[b.kind] || a.id.localeCompare(b.id));
}

/**
 * THE ENQUIRY IS ALWAYS THE FIRST HISTORY ITEM. A lead whose club never texted or called it (a relay-only club) has no intake event, so
 * the history read "Nothing recorded" for a lead that plainly exists. The lead record carries when it arrived and from where.
 * An existing intake entry is left alone; none at all gets one from the record.
 */
export function withEnquiry(entries: LeadHistoryEntry[], lead: { id: string; receivedAt: string; source: string | null } | null): LeadHistoryEntry[] {
  if (!lead || !lead.receivedAt || entries.some((e) => e.kind === 'intake')) return entries;
  return [...entries, { kind: 'intake', id: `lead:${lead.id}`, at: lead.receivedAt, source: lead.source, result: null }];
}

/** The first name shown on a person's text. ⚠️ In the club's view a text Kyck staff sent reads "Kyck" (the club sees no Kyck staff names). */
export function staffNameOf(reply: { surface: 'portal' | 'kyck'; byName: string | null }, forClub: boolean): string | undefined {
  if (forClub && reply.surface === 'kyck') return 'Kyck';
  const first = personFirstName(reply.byName);
  return first ? first : undefined;
}

/** Who a history message is from: the lead, THIS club's bot by name ("the assistant" when it has none), or a system note. */
export function historySpeaker(from: HistoryMessage['from'], assistantName: string | null | undefined): string {
  return from === 'lead' ? 'Lead' : from === 'assistant' ? botNameOf(assistantName) : 'Note';
}

export const cursorOf = (e: LeadHistoryEntry): string => `${e.at}|${e.kind}|${e.id}`;

/** One page of an already-merged list, strictly after `cursor` (older). A bad cursor starts from the top. */
export function paginateHistory(merged: LeadHistoryEntry[], cursor: string | null, limit: number = HISTORY_PAGE_SIZE, assistantName: string | null = null): LeadHistoryPage {
  let start = 0;
  if (cursor) {
    const idx = merged.findIndex((e) => cursorOf(e) === cursor);
    start = idx === -1 ? 0 : idx + 1;
  }
  const entries = merged.slice(start, start + limit);
  const more = start + limit < merged.length;
  return { assistantName, entries, nextCursor: more && entries.length > 0 ? cursorOf(entries[entries.length - 1]!) : null, total: merged.length };
}

// ---------------------------------------------------------------------------
// Sam's "Earlier history" (deterministic: stored data only, no model call)
// ---------------------------------------------------------------------------

export const EARLIER_MAX_ITEMS = 5;
export const EARLIER_MAX_CHARS = 1800;
export const EARLIER_MAX_LEAD_MESSAGES = 3;
export const EARLIER_MESSAGE_CHARS = 140;
export const EARLIER_SUMMARY_CHARS = 280;

export interface EarlierConversation {
  at: string;
  outcome: LeadOutcome;
  /** The lead's own messages in that conversation, oldest first. */
  leadMessages: string[];
}
export interface EarlierCall {
  at: string;
  direction: 'outbound' | 'inbound';
  /** Only a PERSON-confirmed outcome counts as history; null when none was confirmed. */
  confirmedOutcome: LeadOutcome | null;
  summaryText: string | null;
}

const clip = (s: string, n: number): string => {
  const t = s.replace(/\s+/g, ' ').trim();
  return t.length <= n ? t : `${t.slice(0, Math.max(0, n - 1)).trimEnd()}…`;
};
const day = (iso: string): string => iso.slice(0, 10);

/**
 * The short block Sam is given about EARLIER dealings with the same person at the same club, or null when there is
 * none. ⚠️ It is HISTORY, labelled as such in its own heading: not something the lead just said, and not the current
 * enquiry. Newest first, at most EARLIER_MAX_ITEMS items and EARLIER_MAX_CHARS characters in all.
 */
export function buildEarlierHistoryBlock(input: { conversations: EarlierConversation[]; calls: EarlierCall[] }): string | null {
  type Item = { at: string; text: string };
  const items: Item[] = [];
  for (const c of input.conversations) {
    const said = c.leadMessages.slice(-EARLIER_MAX_LEAD_MESSAGES).map((m) => `"${clip(m, EARLIER_MESSAGE_CHARS)}"`);
    items.push({
      at: c.at,
      text: `${day(c.at)}, a text conversation. Outcome: ${LEAD_OUTCOME_LABELS[c.outcome]}.${said.length > 0 ? ` What they wrote: ${said.join(' / ')}` : ''}`,
    });
  }
  for (const c of input.calls) {
    if (!c.confirmedOutcome && !c.summaryText) continue;
    const kind = c.direction === 'outbound' ? 'a call from the club' : 'a call they made to the club';
    items.push({
      at: c.at,
      text: `${day(c.at)}, ${kind}.${c.confirmedOutcome ? ` Outcome: ${LEAD_OUTCOME_LABELS[c.confirmedOutcome]}.` : ''}${c.summaryText ? ` Summary: ${clip(c.summaryText, EARLIER_SUMMARY_CHARS)}` : ''}`,
    });
  }
  if (items.length === 0) return null;
  items.sort((a, b) => ms(b.at) - ms(a.at));
  const header =
    'EARLIER HISTORY WITH THIS PERSON AT THIS CLUB (stored notes about PAST contact, newest first. It is history, NOT the current enquiry and NOT something they just said. Use it only to be familiar and avoid repeating yourself; never quote it back or assume it is still true):';
  const lines: string[] = [];
  let used = header.length;
  for (const it of items.slice(0, EARLIER_MAX_ITEMS)) {
    const line = `- ${it.text}`;
    if (used + line.length + 1 > EARLIER_MAX_CHARS) break;
    lines.push(line);
    used += line.length + 1;
  }
  return lines.length === 0 ? null : [header, ...lines].join('\n');
}
